import { useTheme } from '@emotion/react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useEffect, useRef } from 'react';
import { darkTheme, lightTheme } from '../../../components/highcharts/themes';
import { formatNumber } from '@/utils/formatters';

const PortfolioAnalysisGroupedColumnChart = ({ data, isLoading }) => {
  const chartComponentRef = useRef(null);
  const theme = useTheme();

  // Extract categories (months) from the data
  const months =
    data?.this_year?.map((item) => {
      const [year, month] = item.year_month.split('-'); // Extract year and month
      const date = new Date(year, month - 1); // Convert to Date object
      return date.toLocaleString('da-DK', { month: 'short', year: '2-digit' }); // Get short month name (e.g., "Jan")
    }) || [];

  // Extract consumption values
  const thisYearData = data?.this_year?.map((item) => item.consumption) || [];
  const lastYearData = data?.last_year?.map((item) => item.consumption) || [];

  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    if (chartComponentRef.current?.chart) {
      const chart = chartComponentRef.current.chart;
      chart.update(
        {
          chart: { backgroundColor: themeOptions.chart.backgroundColor },
          time: {
            // timezoneOffset: new Date().getTimezoneOffset('da-DK'),
            useUTC: true,
          },
          title: { style: { color: themeOptions.title.style.color } },
          xAxis: {
            gridLineColor: 'transparent',
            lineColor: themeOptions.xAxis.lineColor,
            labels: { style: { color: themeOptions.xAxis.labels.style.color } },
          },
          yAxis: [
            {
              gridLineColor: 'transparent',
              labels: {
                style: { color: themeOptions.yAxis.labels.style.color },
              },
            },
          ],
          legend: {
            itemStyle: { color: themeOptions.legend.itemStyle.color },
          },
          tooltip: {
            backgroundColor: themeOptions.tooltip.backgroundColor,
            borderColor: themeOptions.tooltip.borderColor,
            style: {
              color: theme.palette.mode === 'dark' ? 'white' : 'black', // ✅ Dynamic text color
            },
          },
        },
        false,
      );
      chart.redraw();
    }
  }, [theme]);
  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      if (isLoading) {
        chart.showLoading('Loading data...');
      } else {
        chart.hideLoading();
      }
    }
  }, [isLoading]);
  const options = {
    chart: {
      type: 'column',
    },
    title: {
      text: ``,
    },
    xAxis: {
      categories: months, // Use extracted months as categories
      labels: {
        formatter: function () {
          if (this.value.includes('maj')) return this.value.split(' ')[0];
          else return this.value.split('.')[0];
        },
      },
      title: {
        text: 'Måned',
      },
    },
    yAxis: {
      min: 0,
      title: {
        text: 'Total [kWh]',
      },
    },
    series: [
      {
        name: 'Seneste 12 måneder',
        data: thisYearData,
        tooltip: {
          headerFormat: '',
          valueDecimals: 0,
          valueSuffix: ' kWh',
          pointFormatter: function () {
            return `<span>${
              this.category
            }</span><br/><span>${'Total forbrug'}</span>: <b>${formatNumber(
              this.y,
            )} kWh</b><br/>`;
          },
        },
      },
      {
        name: '12-24 måneder',
        data: lastYearData,
        tooltip: {
          headerFormat: '',
          valueDecimals: 0,
          valueSuffix: ' kWh',
          pointFormatter: function () {
            return `<span>${this.category.split(' ')[0]} ${
              Number(this.category.split(' ')[1]) - 1
            }</span><br/><span>${'Total forbrug'}</span>: <b>${formatNumber(
              this.y,
            )} kWh</b><br/>`;
          },
        },
      },
    ],
  };

  return (
    <div style={{ width: '100%', marginTop: 2 }}>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
      />
    </div>
  );
};

export default PortfolioAnalysisGroupedColumnChart;
